import React, { useMemo } from 'react'
import './ChapterChart.css'
import { ROMANS_1_DATA, ChartData, ArcRelationship } from '../data/romans1'

// Relationship symbols (Fuller / Piper arcing taxonomy)
const REL_SYMBOL: Record<ArcRelationship, string> = {
  grounds:     'G',
  inference:   '∴',
  purpose:     'P',
  result:      'R',
  progression: '+',
  explanation: '=',
  contrast:    '≠',
  series:      '·',
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** First N words of a verse followed by ellipsis if truncated */
const keyPhrase = (text: string, words = 7): string => {
  const parts = text.trim().split(/\s+/)
  if (parts.length <= words) return text
  return parts.slice(0, words).join(' ') + '…'
}

const pluralVerses = (n: number) => `${n} ${n === 1 ? 'verse' : 'verses'}`

// ── Component ─────────────────────────────────────────────────────────────────

const ChapterChart: React.FC<{ data?: ChartData }> = ({ data = ROMANS_1_DATA }) => {
  const d = data

  const totalVerses = useMemo(
    () => d.paragraphs.reduce((sum, p) => sum + (p.endVerse - p.startVerse + 1), 0),
    [d.paragraphs]
  )

  const firstVerse = d.paragraphs[0]?.startVerse ?? 1

  return (
    <div className="cc-wrap">
      <div className="cc-page">

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <header className="cc-header">
          <div className="cc-header-left">
            <div className="cc-kicker">Chapter Chart · {d.classification}</div>
            <h1 className="cc-book-title">{d.book} {d.chapter}</h1>
          </div>
          <div className="cc-header-right">
            {totalVerses} verses · {d.paragraphs.length} paragraphs
            {d.arcs && ` · ${d.arcs.length} arcs`}
          </div>
        </header>

        {/* ── Meta Banner ─────────────────────────────────────────────────── */}
        <div className="cc-meta-banner">
          <div className="cc-meta-field">
            <div className="cc-meta-key">Genre</div>
            <div className="cc-meta-val">{d.classification}</div>
          </div>
          <div className="cc-meta-field">
            <div className="cc-meta-key">Theme</div>
            <div className="cc-meta-val">{d.unity}</div>
          </div>
          <div className="cc-meta-field">
            <div className="cc-meta-key">Purpose</div>
            <div className="cc-meta-val">{d.problem}</div>
          </div>
        </div>

        {/* ── Body ────────────────────────────────────────────────────────── */}
        <div className="cc-body">

          {/* ── Vertical Timeline ──────────────────────────────────────── */}
          <div className="cc-timeline">

            {/* Arc / Movement brackets */}
            {(d.arcs ?? []).map((mov, i) => {
              const versesBeforeStart = mov.startVerse - firstVerse
              const movVerseCount     = mov.endVerse - mov.startVerse + 1
              const topPct    = (versesBeforeStart / totalVerses) * 100
              const heightPct = (movVerseCount     / totalVerses) * 100
              const isSecondary = mov.accent === 'secondary'
              return (
                <div
                  key={i}
                  className={`cc-arc-bracket${isSecondary ? ' secondary' : ''}`}
                  style={{ top: `${topPct}%`, height: `${heightPct}%` }}
                >
                  <span className="cc-arc-label">
                    {mov.relationship && (
                      <span className="cc-arc-rel-sym">{REL_SYMBOL[mov.relationship]}</span>
                    )}
                    {mov.label}
                  </span>
                </div>
              )
            })}

            {/* Paragraph cards */}
            {d.paragraphs.map(para => {
              const verseCount = para.endVerse - para.startVerse + 1
              const refLabel   = para.startVerse === para.endVerse
                ? `v.${para.startVerse}`
                : `vv.${para.startVerse}–${para.endVerse}`

              return (
                <div
                  key={para.id}
                  className="cc-para"
                  style={{ flexGrow: verseCount }}
                >
                  <div className={`cc-card${para.isKeyThesis ? ' key-thesis' : ''}`}>

                    <div className="cc-card-head">
                      <span className="cc-para-num">
                        ¶{para.number}{para.isKeyThesis ? ' ★' : ''}
                      </span>
                      <span className="cc-para-title">{para.title}</span>
                      <span className="cc-para-ref">{refLabel}</span>
                    </div>

                    {/* Card body: prefer summary, fall back to verse keywords */}
                    <div className="cc-card-body">
                      {para.summary
                        ? para.summary
                        : (para.verses || []).map(v => (
                            <span key={v.number} className="cc-vk" title={v.text}>
                              <sup>{v.number}</sup>{keyPhrase(v.text)}
                            </span>
                          ))
                      }
                    </div>

                    <div className="cc-card-foot">
                      <span className={`cc-tag${para.isKeyThesis ? ' key' : ''}`}>
                        {para.topic}
                      </span>
                      <span className="cc-vcount">{pluralVerses(verseCount)}</span>
                    </div>

                  </div>
                </div>
              )
            })}
          </div>

          {/* ── Sidebar ────────────────────────────────────────────────── */}
          <aside className="cc-sidebar">

            {/* Key Terms */}
            {d.keyTerms.length > 0 && (
              <div className="cc-side-card">
                <div className="cc-side-h">Key Terms</div>
                {d.keyTerms.map((kt, i) => (
                  <div key={i} className="cc-term">
                    <div className="cc-term-head">
                      <span className="cc-term-name">{kt.term}</span>
                      <span className="cc-term-ref">v.{kt.verses.join(', ')}</span>
                    </div>
                    <div className="cc-term-def">{kt.definition}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Interpretive Notes + ruled notes space */}
            <div className="cc-interp-card">
              <div className="cc-side-h">Interpretive Notes</div>
              {d.interpretiveNotes && (
                <div className="cc-interp-body">{d.interpretiveNotes}</div>
              )}
              <div className="cc-user-notes" />
            </div>

            {/* Legend — only shown when movements exist */}
            {(d.arcs ?? []).length > 0 && (
              <div className="cc-legend-card">
                <div className="cc-legend-rows">
                  {(d.arcs ?? []).map((mov, i) => (
                    <div key={i} className="cc-legend-row">
                      <span className={`cc-legend-bar${mov.accent === 'secondary' ? ' secondary' : ''}`} />
                      Arc · {mov.label}
                    </div>
                  ))}
                </div>
              </div>
            )}

          </aside>
        </div>

      </div>
    </div>
  )
}

export default ChapterChart
